import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import ApprenantParrainagePanel from '../../components/apprenant/ApprenantParrainagePanel';
import PortalWalletPanel from '../../components/shared/PortalWalletPanel';
import PortalNotificationsPanel from '../../components/shared/PortalNotificationsPanel';
import AmbassadorDashboardPanel from '../../components/ambassadeur/AmbassadorDashboardPanel';
import AmbassadorProfilPanel from '../../components/ambassadeur/AmbassadorProfilPanel';
import FormateurChangePasswordPanel from '../../components/formateur/FormateurChangePasswordPanel';
import { authService } from '../../services/authService';
import type { AdminBreadcrumbSegment } from '../../components/admin/AdminBreadcrumb';
import type { AdminNavItem } from '../../types/admin';

const NAV: AdminNavItem[] = [
  { id: 'dashboard', label: 'Accueil', icon: 'dashboard', path: '/ambassadeur' },
  { id: 'parrainage', label: 'Parrainage', icon: 'groups', path: '/ambassadeur/parrainage' },
  { id: 'wallet', label: 'Portefeuille', icon: 'account_balance_wallet', path: '/ambassadeur/wallet' },
  { id: 'profil', label: 'Mon profil', icon: 'person', path: '/ambassadeur/profil' },
];

const BREADCRUMB_LABELS: Record<string, string> = {
  dashboard: 'Accueil',
  parrainage: 'Parrainage',
  wallet: 'Portefeuille',
  profil: 'Mon profil',
  password: 'Mot de passe',
  notifications: 'Notifications',
};

function isAmbassadeurPasswordPath(pathname: string) {
  return (
    pathname === '/ambassadeur/changer-mot-de-passe' ||
    pathname.startsWith('/ambassadeur/changer-mot-de-passe/')
  );
}

function isAmbassadeurProfilPath(pathname: string) {
  return pathname === '/ambassadeur/profil' || pathname.startsWith('/ambassadeur/profil/');
}

type AmbassadeurUser = {
  telephone?: string;
  mustCompleteProfile?: boolean;
  mustChangePassword?: boolean;
  profil?: { prenom?: string; nom?: string; photoUrl?: string | null };
};

type Props = {
  user: AmbassadeurUser;
  onLogout: () => void;
  onUserUpdated?: (user: AmbassadeurUser) => void;
};

function AmbassadeurOnboardingGuard({
  user,
  children,
}: {
  user: AmbassadeurUser;
  children: ReactNode;
}) {
  const location = useLocation();
  const needsOnboarding = user.mustCompleteProfile || user.mustChangePassword;
  const onProfil = location.pathname.startsWith('/ambassadeur/profil');
  const onPassword = location.pathname.startsWith('/ambassadeur/changer-mot-de-passe');

  if (needsOnboarding && !onProfil && !onPassword) {
    return <Navigate to="/ambassadeur/profil" replace />;
  }
  return <>{children}</>;
}

export default function AmbassadeurAcademiaPage({ user, onLogout, onUserUpdated }: Props) {
  const location = useLocation();
  const fullName = [user.profil?.prenom, user.profil?.nom].filter(Boolean).join(' ');

  const refreshUser = async () => {
    const profile = await authService.getCurrentUser();
    if (profile && onUserUpdated) onUserUpdated(profile);
  };

  const viewKey = useMemo(() => {
    if (isAmbassadeurPasswordPath(location.pathname)) return 'password';
    if (isAmbassadeurProfilPath(location.pathname)) return 'profil';
    if (location.pathname.startsWith('/ambassadeur/parrainage')) return 'parrainage';
    if (location.pathname.startsWith('/ambassadeur/wallet')) return 'wallet';
    if (location.pathname.startsWith('/ambassadeur/notifications')) return 'notifications';
    return 'dashboard';
  }, [location.pathname]);

  const breadcrumb: AdminBreadcrumbSegment[] = useMemo(
    () => [
      { label: 'Espace ambassadeur' },
      { label: BREADCRUMB_LABELS[viewKey] ?? 'Accueil' },
    ],
    [viewKey],
  );

  return (
    <AdminLayout
      navigation={NAV}
      user={{
        name: fullName || user.telephone || 'Ambassadeur',
        subtitle: 'Ambassadeur JCONSULT',
        photoUrl: user.profil?.photoUrl ?? null,
        prenom: user.profil?.prenom,
        nom: user.profil?.nom,
      }}
      breadcrumb={breadcrumb}
      onLogout={onLogout}
      searchQuery=""
      onSearchChange={() => {}}
      brandAlt="JCONSULT MY Academia"
    >
      <AmbassadeurOnboardingGuard user={user}>
        <Routes>
          <Route path="/" element={<AmbassadorDashboardPanel user={user} />} />
          <Route path="/parrainage" element={<ApprenantParrainagePanel portal="ambassadeur" user={user} />} />
          <Route
            path="/wallet"
            element={<PortalWalletPanel portal="ambassadeur" parrainagePath="/ambassadeur/parrainage" />}
          />
          <Route
            path="/profil"
            element={
              <AmbassadorProfilPanel
                user={user}
                onboarding={Boolean(user.mustCompleteProfile)}
                onProfileSaved={refreshUser}
              />
            }
          />
          <Route
            path="/changer-mot-de-passe"
            element={
              <FormateurChangePasswordPanel
                user={user}
                onboarding={Boolean(user.mustChangePassword)}
                onPasswordChanged={refreshUser}
                homePath="/ambassadeur"
                profilPath="/ambassadeur/profil"
              />
            }
          />
          <Route path="/notifications" element={<PortalNotificationsPanel portal="partenaire" />} />
          <Route path="*" element={<Navigate to="/ambassadeur" replace />} />
        </Routes>
      </AmbassadeurOnboardingGuard>
    </AdminLayout>
  );
}
